import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { ConvoSessionNote, ConvoNoteType, ConvoNoteAttachment } from '../../types/convo.js';
import * as convoApi from '../../services/convo-api.js';

const NOTE_TYPE_LABELS: Record<ConvoNoteType, string> = {
  general: 'Allgemein',
  strategy: 'Strategie',
  observation: 'Beobachtung',
  followup: 'Follow-up',
};

const ROLE_LABELS: Record<string, string> = {
  user: 'Du',
  assistant: 'Coach',
  system: 'System',
};

@customElement('cp-convo-session-notes')
export class CpConvoSessionNotes extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Array }) notes: ConvoSessionNote[] = [];
  @property({ type: Number }) sessionId = 0;

  @state() private _newContent = '';
  @state() private _newNoteType: ConvoNoteType = 'general';
  @state() private _submitting = false;
  @state() private _sendingToCoach = false;
  @state() private _error: string | null = null;
  @state() private _pendingAttachments: ConvoNoteAttachment[] = [];
  @state() private _isUploading = false;

  private async _handleFiles(files: FileList | File[]): Promise<void> {
    if (!this.sessionId) return;
    this._isUploading = true;
    this._error = null;
    try {
      for (const file of Array.from(files)) {
        if (!file.type.startsWith('image/')) continue;
        const att = await convoApi.uploadConvoAttachment(this.sessionId, file);
        this._pendingAttachments = [...this._pendingAttachments, att];
      }
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Upload fehlgeschlagen';
    } finally {
      this._isUploading = false;
    }
  }

  private _onAttachClick(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    input.addEventListener('change', () => {
      if (input.files?.length) this._handleFiles(input.files);
    });
    input.click();
  }

  private _onPaste(e: ClipboardEvent): void {
    const items = e.clipboardData?.items;
    if (!items) return;
    const imageFiles: File[] = [];
    for (const item of Array.from(items)) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) imageFiles.push(file);
      }
    }
    if (imageFiles.length > 0) {
      e.preventDefault();
      this._handleFiles(imageFiles);
    }
  }

  private async _onRemoveAttachment(id: number): Promise<void> {
    try {
      await convoApi.deleteConvoAttachment(id);
    } catch { /* ignore */ }
    this._pendingAttachments = this._pendingAttachments.filter(a => a.id !== id);
  }

  private _getPendingAttachmentIds(): number[] {
    return this._pendingAttachments.map(a => a.id);
  }

  private _clearPending(): void {
    this._pendingAttachments = [];
  }

  private async _addNote(): Promise<void> {
    if (!this._newContent.trim() || !this.sessionId) return;

    this._submitting = true;
    this._error = null;
    try {
      const ids = this._getPendingAttachmentIds();
      await convoApi.addNote(this.sessionId, {
        role: 'user',
        content: this._newContent.trim(),
        note_type: this._newNoteType,
      }, ids.length > 0 ? ids : undefined);
      this._newContent = '';
      this._newNoteType = 'general';
      this._clearPending();
      this.dispatchEvent(new CustomEvent('note-added', { bubbles: true, composed: true }));
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Notiz konnte nicht hinzugefuegt werden';
    } finally {
      this._submitting = false;
    }
  }

  private async _sendToCoach(): Promise<void> {
    if (!this._newContent.trim() || !this.sessionId) return;

    this._sendingToCoach = true;
    this._error = null;
    try {
      const ids = this._getPendingAttachmentIds();
      await convoApi.sendToCoach(this.sessionId, this._newContent.trim(), ids.length > 0 ? ids : undefined);
      this._newContent = '';
      this._newNoteType = 'general';
      this._clearPending();
      this.dispatchEvent(new CustomEvent('note-added', { bubbles: true, composed: true }));
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Coach-Anfrage fehlgeschlagen';
    } finally {
      this._sendingToCoach = false;
    }
  }

  private async _deleteNote(noteId: number): Promise<void> {
    try {
      await convoApi.deleteNote(this.sessionId, noteId);
      this.dispatchEvent(new CustomEvent('note-added', { bubbles: true, composed: true }));
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Notiz konnte nicht geloescht werden';
    }
  }

  private _formatTime(dateStr: string): string {
    const d = new Date(dateStr);
    return d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  }

  private _isImage(mime: string): boolean {
    return mime.startsWith('image/');
  }

  render() {
    return html`
      <div class="cp-convo-notes">
        <h3 class="cp-convo-notes__title">Notizen & Verlauf</h3>

        <div class="cp-convo-notes__add">
          <textarea
            class="cp-convo-notes__input"
            rows="3"
            placeholder="Neue Notiz hinzufuegen..."
            .value=${this._newContent}
            @input=${(e: Event) => { this._newContent = (e.target as HTMLTextAreaElement).value; }}
            @paste=${(e: ClipboardEvent) => this._onPaste(e)}
            ?disabled=${this._submitting || this._sendingToCoach}
          ></textarea>

          ${this._pendingAttachments.length > 0 || this._isUploading ? html`
            <div class="cp-convo-notes__pending-strip">
              ${this._pendingAttachments.map(att => html`
                <div class="cp-convo-notes__pending-thumb">
                  <img src="${convoApi.getConvoAttachmentUrl(att.id)}" alt="${att.file_name}" />
                  <button class="cp-convo-notes__pending-remove" @click=${() => this._onRemoveAttachment(att.id)} title="Entfernen">&times;</button>
                </div>
              `)}
              ${this._isUploading ? html`<div class="cp-convo-notes__upload-indicator">...</div>` : nothing}
            </div>
          ` : nothing}

          <div class="cp-convo-notes__add-actions">
            <button
              class="cp-convo-notes__attach-btn"
              @click=${this._onAttachClick}
              ?disabled=${this._submitting || this._sendingToCoach || this._isUploading}
              title="Bild anhaengen"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
              </svg>
            </button>
            <select
              class="cp-convo-notes__type-select"
              .value=${this._newNoteType}
              @change=${(e: Event) => { this._newNoteType = (e.target as HTMLSelectElement).value as ConvoNoteType; }}
              ?disabled=${this._submitting || this._sendingToCoach}
            >
              ${Object.entries(NOTE_TYPE_LABELS).map(([val, label]) => html`
                <option value=${val} ?selected=${this._newNoteType === val}>${label}</option>
              `)}
            </select>
            <button
              class="cp-convo-notes__coach-btn"
              @click=${this._sendToCoach}
              ?disabled=${this._submitting || this._sendingToCoach || (!this._newContent.trim() && this._pendingAttachments.length === 0)}
            >
              ${this._sendingToCoach ? 'Coach denkt nach...' : 'An Coach senden'}
            </button>
            <button
              class="cp-convo-notes__add-btn"
              @click=${this._addNote}
              ?disabled=${this._submitting || this._sendingToCoach || (!this._newContent.trim() && this._pendingAttachments.length === 0)}
            >
              ${this._submitting ? 'Wird gespeichert...' : 'Hinzufuegen'}
            </button>
          </div>
        </div>

        ${this._error ? html`<div class="cp-convo-notes__error">${this._error}</div>` : nothing}

        ${this.notes.length === 0 ? html`
          <p class="cp-convo-notes__empty">Noch keine Notizen vorhanden</p>
        ` : html`
          <div class="cp-convo-notes__timeline">
            ${this.notes.map(note => this._renderNote(note))}
            ${this._sendingToCoach ? html`
              <div class="cp-convo-note cp-convo-note--assistant cp-convo-note--loading">
                <div class="cp-convo-note__header">
                  <span class="cp-convo-note__role">Coach</span>
                  <span class="cp-convo-note__type">Follow-up</span>
                </div>
                <div class="cp-convo-note__content cp-convo-note__content--loading">Coach denkt nach...</div>
              </div>
            ` : nothing}
          </div>
        `}
      </div>
    `;
  }

  private _renderNote(note: ConvoSessionNote) {
    return html`
      <div class="cp-convo-note cp-convo-note--${note.role}">
        <div class="cp-convo-note__header">
          <span class="cp-convo-note__role">${ROLE_LABELS[note.role] ?? note.role}</span>
          <span class="cp-convo-note__type">${NOTE_TYPE_LABELS[note.note_type]}</span>
          <span class="cp-convo-note__time">${this._formatTime(note.created_at)}</span>
          <button class="cp-convo-note__delete" @click=${() => this._deleteNote(note.id)} title="Loeschen">&times;</button>
        </div>
        <div class="cp-convo-note__content">${note.content}</div>
        ${note.attachments?.length ? html`
          <div class="cp-convo-note__attachments">
            ${note.attachments.map(att => this._isImage(att.mime_type) ? html`
              <a href="${convoApi.getConvoAttachmentUrl(att.id)}" target="_blank" rel="noopener">
                <img class="cp-convo-note__att-img" src="${convoApi.getConvoAttachmentUrl(att.id)}" alt="${att.file_name}" />
              </a>
            ` : html`
              <a href="${convoApi.getConvoAttachmentUrl(att.id)}" target="_blank" rel="noopener">${att.file_name}</a>
            `)}
          </div>
        ` : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-convo-session-notes': CpConvoSessionNotes;
  }
}
